import { FormikProvider, useFormik } from 'formik';
import moment from 'moment';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouteMatch } from 'react-router';
import { Redirect, Route, Switch, useHistory } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';

import * as graphql from '@treams/treams-network';
import { Subheading } from '@treams/treams-ui';

import LearningEventsSlider from '../components/LearningEventsSlider';
import LearningExploreViewToggle from '../components/LearningExploreViewToggle';
import LearningFilterSidebar from '../components/LearningFilterSidebar';
import LearningCalendar from '../components/calendar/LearningCalendar';

const LearningCalendarRedirect = () => {
	const now = moment();

	return <Redirect to={`/learning/calendar/${now.year()}/${now.month() + 1}`} />;
};

const LearningCalendarDetail = () => {
	const { t } = useTranslation();
	const { params } = useRouteMatch<{ year: string; month: string }>();
	const monthMoment = moment(`${params.year}-${params.month}`, 'YYYY-MM');
	const history = useHistory();

	const formik = useFormik({
		initialValues: {
			types: [],
			subjects: [],
		},
		onSubmit: () => {},
	});

	const [fromDate, setFromDate] = useState<Date>(moment(monthMoment).subtract(4, 'days').toDate());
	const [untilDate, setUntilDate] = useState<Date>(
		moment(monthMoment).add(1, 'month').add(4, 'days').toDate(),
	);

	const { data } = graphql.useLearningEventsQuery({
		variables: {
			first: 100,
			filters: {
				learningTypeIDs: formik.values.types,
				learningSubjectIDs: formik.values.subjects,
				fromDate,
				untilDate,
			},
		},
	});

	const learningEvents = data?.learningEvents.edges
		.map(({ node }) => node)
		.filter((learningEvent) => learningEvent.isViewerAttending || learningEvent.viewerCanJoin);

	return (
		<>
			<Box mb={5}>
				<LearningEventsSlider title={t('learning.events.upcoming.title')} />
			</Box>
			<Flex mb={3} justifyContent="space-between" alignItems="center">
				<Subheading mb={0}>{t('learning.events.explore.title')}</Subheading>
				<LearningExploreViewToggle />
			</Flex>
			<Flex mt={4} flexWrap="wrap">
				<Box width={[1, 1, 1 / 3, 1 / 4]} pr={[0, 0, 3, 4]}>
					<FormikProvider value={formik}>
						<LearningFilterSidebar />
					</FormikProvider>
				</Box>

				<Box width={[1, 1, 2 / 3, 3 / 4]}>
					<LearningCalendar
						events={(learningEvents as graphql.LearningEvent[]) ?? []}
						year={parseInt(params.year, 10)}
						month={parseInt(params.month, 10)}
						onNext={() => {
							const nextMonth = monthMoment.clone().add(1, 'months');
							setFromDate(moment(fromDate).add(1, 'month').toDate());
							setUntilDate(moment(untilDate).add(1, 'month').toDate());

							history.push(`/learning/calendar/${nextMonth.year()}/${nextMonth.month() + 1}`);
						}}
						onPrev={() => {
							const prevMonth = monthMoment.clone().subtract(1, 'months');
							setFromDate(moment(fromDate).subtract(1, 'month').toDate());
							setUntilDate(moment(untilDate).subtract(1, 'month').toDate());

							history.push(`/learning/calendar/${prevMonth.year()}/${prevMonth.month() + 1}`);
						}}
					/>
				</Box>
			</Flex>
		</>
	);
};

const LearningCalendarContainer = () => {
	return (
		<Switch>
			<Route path="/learning/calendar" exact>
				<LearningCalendarRedirect />
			</Route>

			<Route path="/learning/calendar/:year/:month">
				<LearningCalendarDetail />
			</Route>
		</Switch>
	);
};

export default LearningCalendarContainer;
